"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useRef } from "react";
import { MapPin, Calendar, CircleCheck as CheckCircle2, ArrowRight, ShieldCheck, Star, TrendingUp, UserCheck, Sparkles } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link } from "@/components/common/Link";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
gsap.registerPlugin(useGSAP);
const trustPoints = [{
  id: 1,
  text: "Direct Consultant Access — No account managers"
}, {
  id: 2,
  text: "Tailored Local SEO Strategy for Northamptonshire"
}, {
  id: 3,
  text: "Transparent Reporting & Practical Implementation"
}];
const highlightStats = [{
  id: 1,
  label: "SEO Experience",
  value: "10+ Yrs"
}, {
  id: 2,
  label: "Freelance Model",
  value: "100% Direct"
}, {
  id: 3,
  label: "Client Retention",
  value: "High Trust"
}];
export default function Heroauthority() {
  const containerRef = useRef<HTMLDivElement>(null);
  useGSAP(() => {
    const el = containerRef.current;
    if (!el) return;
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.set(".hero-animate", {
        y: 20
      });
      gsap.to(".hero-animate", {
        opacity: 1,
        y: 0,
        duration: 0.7,
        stagger: 0.1,
        ease: "power2.out"
      });
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set(".hero-animate", {
        opacity: 1,
        y: 0
      });
    });
  }, {
    scope: containerRef
  });
  return <section id="herolocalauthority" data-nav="dark" ref={containerRef} className="relative bg-primary text-primary-foreground overflow-hidden py-16 md:py-24 lg:py-28">
      <div className="absolute inset-0 pointer-events-none opacity-20">
        <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-accent blur-3xl" />
        <div className="absolute bottom-0 right-0 w-80 h-80 rounded-full bg-accent/50 blur-3xl" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          
          <div className="lg:col-span-7 flex flex-col space-y-6 text-left">
            
            <div className="hero-animate" style={{
            opacity: 0
          }}>
              <Badge variant="outline" className="bg-primary-foreground/10 border-primary-foreground/20 text-primary-foreground px-3.5 py-1.5 text-xs sm:text-sm font-medium rounded-full inline-flex items-center gap-2">
                <MapPin className="size-4 text-accent" />
                <span>Local SEO Consultant in Northampton</span>
              </Badge>
            </div>

            <h2 className="hero-animate font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-primary-foreground leading-tight sm:leading-tight" style={{
            opacity: 0
          }}>
              Professional SEO Consultant for Sustainable Search Growth
            </h2>

            <p className="hero-animate text-base sm:text-lg md:text-xl text-primary-foreground/90 font-sans max-w-2xl leading-relaxed" style={{
            opacity: 0
          }}>
              Hi, I{"'"}m Yassine Essaadi. I help local and national businesses in Northampton dominate organic search. When you work with YE Consultancy, you work directly with me—no agency middlemen, no junior account managers, just hands-on technical and strategic SEO expertise.
            </p>

            <div className="hero-animate space-y-3 py-2" style={{
            opacity: 0
          }}>
              {trustPoints.map((point, i) => <div data-index={i} key={point.id} className="flex items-center gap-3 text-sm sm:text-base text-primary-foreground/95">
                  <CheckCircle2 className="size-5 shrink-0 text-accent" />
                  <span>{point.text}</span>
                </div>)}
            </div>

            <div className="hero-animate flex flex-col sm:flex-row items-stretch sm:items-center gap-4 pt-4" style={{
            opacity: 0
          }}>
              <Button size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 font-medium px-6 py-6 text-base rounded-lg shadow-md cursor-pointer transition-transform hover:-translate-y-0.5" asChild>
                <Link to="/ContactPage">
                  <SafeHydrate><Calendar className="size-5 mr-2" /></SafeHydrate>
                  <span>Book a Free SEO Consultation</span>
                </Link>
              </Button>

              <Button size="lg" variant="outline" className="border-primary-foreground/30 bg-primary-foreground/5 text-primary-foreground hover:bg-primary-foreground/15 font-medium px-6 py-6 text-base rounded-lg shadow-sm cursor-pointer" asChild>
                <Link to="/CaseStudiesPage">
                  <span>Explore My Case Studies</span>
                  <ArrowRight className="size-5 ml-2" />
                </Link>
              </Button>
            </div>

            <div className="hero-animate flex items-center gap-2 text-xs text-primary-foreground/80 pt-1" style={{
            opacity: 0
          }}>
              <ShieldCheck className="size-4 text-accent" />
              <span>Free initial discovery call • Honest technical assessment • No obligation</span>
            </div>

          </div>

          <div className="lg:col-span-5 flex justify-center lg:justify-end">
            <div className="hero-animate relative w-full max-w-md lg:max-w-none" style={{
            opacity: 0
          }}>
              
              <div className="relative rounded-2xl overflow-hidden border border-primary-foreground/20 bg-primary-foreground/10 backdrop-blur-sm p-3 shadow-2xl">
                <div className="relative rounded-xl overflow-hidden aspect-4/5 bg-primary-foreground/5">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi.webp-wvc-sizes" alt="Yassine Essaadi - Independent SEO Consultant in Northampton" className="w-full h-full object-cover object-center" loading="eager" />
                  <div className="absolute inset-0 bg-gradient-to-t from-primary/90 via-primary/20 to-transparent" />

                  <div className="absolute bottom-4 left-4 right-4 p-4 rounded-xl bg-primary/80 backdrop-blur-md border border-primary-foreground/20 text-primary-foreground">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-serif font-bold text-lg text-primary-foreground">
                          Yassine Essaadi
                        </h3>
                        <p className="text-xs text-primary-foreground/80 font-sans">
                          Independent SEO Consultant
                        </p>
                      </div>
                      <Badge className="bg-accent text-accent-foreground text-xs px-2.5 py-1">
                        100% Direct
                      </Badge>
                    </div>
                  </div>
                </div>

                <div className="absolute -top-4 -right-4 hidden sm:flex items-center gap-2 bg-card text-card-foreground p-3 rounded-xl border border-border shadow-lg">
                  <UserCheck className="size-5 text-primary" />
                  <div className="text-xs text-left">
                    <p className="font-semibold text-foreground">You work directly with me</p>
                    <p className="text-muted-foreground">No junior team handoffs</p>
                  </div>
                </div>

                <div className="absolute -bottom-4 -left-4 hidden sm:flex items-center gap-2 bg-card text-card-foreground p-3 rounded-xl border border-border shadow-lg">
                  <Sparkles className="size-5 text-accent" />
                  <div className="text-xs text-left">
                    <p className="font-semibold text-foreground">Northamptonshire Focus</p>
                    <p className="text-muted-foreground">National & Local SEO Specialist</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2 mt-6 pt-2">
                {highlightStats.map((stat, i) => <div data-index={i} key={stat.id} className="bg-primary-foreground/10 border border-primary-foreground/15 rounded-xl p-3 text-center">
                    <div className="text-lg font-bold font-serif text-accent">
                      {stat.value}
                    </div>
                    <div className="text-[13px] text-primary-foreground/80 font-sans mt-0.5">
                      {stat.label}
                    </div>
                  </div>)}
              </div>

            </div>
          </div>

        </div>
      </div>
    </section>;
}